import React from 'react';
import { Icon } from './Icon';

export interface CheckboxProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label?: string;
    disabled?: boolean;
    /** When true, the label sits to the LEFT of the box (toolbar style). Default: right (form style). */
    labelPosition?: 'left' | 'right';
    className?: string;
    title?: string;
    /** Visually mixed/partial state (e.g. a "select all" header with some rows checked). */
    indeterminate?: boolean;
    /**
     * Render ONLY the circular control (no wrapping `<label>` / text), so it can
     * be nested inside a caller's own `<label>` that carries rich content —
     * avoids invalid nested labels. The outer label toggles the input natively.
     */
    bare?: boolean;
    /**
     * `sm` (default): the compact form/toolbar check, 12 px muted label.
     * `md`: a choice that IS the form's content (what to export…) — a 20 px
     * circle and the label in the type of a `ChoiceCard` title, so it sits
     * level with the cards beside it.
     */
    size?: 'sm' | 'md';
}

/**
 * Standard labeled checkbox. The visual is a **circular** indicator that matches
 * the selected-option check in `DropdownSelect` (primary-filled circle with a
 * white check) — the single check style used across the whole app (tenant +
 * admin). The native `<input>` is kept (visually hidden) for accessibility and
 * keyboard support; the circle is the rendered control.
 *
 * Use everywhere a binary toggle is needed (filter toolbars, modal options,
 * grids, list selectors). Never reinvent a raw `<input type="checkbox">`.
 */
const Checkbox: React.FC<CheckboxProps> = ({
    checked,
    onChange,
    label,
    disabled,
    labelPosition = 'right',
    className = '',
    title,
    indeterminate = false,
    bare = false,
    size = 'sm',
}) => {
    const active = checked || indeterminate;
    const md = size === 'md';
    const box = (
        <span className={`relative inline-flex shrink-0 items-center justify-center ${bare ? className : ''}`}>
            <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
                ref={(el) => {
                    if (el) el.indeterminate = indeterminate;
                }}
                className="peer sr-only"
            />
            <span
                aria-hidden="true"
                className={`flex ${md ? 'h-5 w-5 border-2' : 'h-4 w-4 border'} items-center justify-center rounded-full transition-colors ${
                    active
                        ? 'border-primary bg-primary'
                        : 'border-border-strong bg-transparent dark:border-border-strong-dark'
                }`}
            >
                {indeterminate ? (
                    <span className={`h-0.5 ${md ? 'w-2.5' : 'w-2'} rounded-full bg-white`} />
                ) : checked ? (
                    <Icon name="check" className={`leading-none text-white ${md ? '!text-[14px]' : '!text-[12px]'}`} />
                ) : null}
            </span>
        </span>
    );

    if (bare) {
        return box;
    }

    if (!label) {
        return (
            <label
                className={`inline-flex items-center cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
                title={title}
            >
                {box}
            </label>
        );
    }

    return (
        <label
            title={title}
            className={`inline-flex items-center ${md ? 'gap-3 font-semibold text-text-main dark:text-white' : 'gap-1.5 text-xs text-slate-600 dark:text-slate-400'} cursor-pointer select-none whitespace-nowrap ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
        >
            {labelPosition === 'left' && <span>{label}</span>}
            {box}
            {labelPosition === 'right' && <span>{label}</span>}
        </label>
    );
};

export default Checkbox;
